import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { changeDealData, changeMeta, changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';

import { getMetaData } from 'modules/api/deal';

import { cancelFunctions } from 'utils/cancelFunctions';

import ToggleSwitch from 'lib/ToggleSwitch';
import { Label } from 'lib/Label';
import Button, { Color } from 'lib/Button';
import Chip from 'lib/Chip';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';

import plusSVG from 'assets/icons/plus.svg';

import { Size, TextPriority } from 'lib/Chip/Chip.types';
import { Store } from 'components/common/types/Store.types';
import { DirectSalesExclusionFlag } from 'components/common/types/DirectSalesDeal.types';

const Juxtaposition = (): React.ReactElement => {
  const dispatch = useDispatch();

  const meta = useSelector((state: Store) => state.dealManagement.meta);
  const juxtapositionExclusion = useSelector((state: Store) => state.dealManagement.commonDeal.juxtapositionExclusion);
  const productCategories = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.productCategories);
  const organisations = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.organisations);

  const isJuxtapositionOn = juxtapositionExclusion === DirectSalesExclusionFlag.ON;

  const onJuxtapositionChange = (): void => {
    dispatch(
      changeDealData({
        juxtapositionExclusion: isJuxtapositionOn ? DirectSalesExclusionFlag.OFF : DirectSalesExclusionFlag.ON,
      }),
    );
  };

  const onRemove = (productCategoriesToRemove: string[]): void => {
    const values = new Set(productCategoriesToRemove);
    const filtered = productCategories.filter((category) => !values.has(category.name));
    dispatch(changeDealData({ exclusions: { organisations, productCategories: filtered } }));
  };

  useEffect(() => {
    const getMeta = async (): Promise<void> => {
      try {
        const metaData = await getMetaData({ cancelFunctions });
        dispatch(changeMeta({ ...meta, productCategoryGroups: metaData.productCategoryGroups }));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Error fetching meta data: ${error}`);
      }
    };
    if (!meta.productCategoryGroups.length) {
      getMeta();
    }
  }, [meta]);

  return (
    <div className="w-full py-1 border-b border-neutral-950-opacity-10">
      <div className="flex w-full justify-between items-center py-1">
        <Label id="juxtaposition-label" label="Juxtaposition" className="mb-0 font-semibold" />
        <ToggleSwitch
          isChecked={isJuxtapositionOn}
          onChange={() => onJuxtapositionChange()}
          dataTestId="juxtaposition-switch"
        />
      </div>
      {isJuxtapositionOn && (
        <div className="flex w-full justify-between items-start">
          <Button
            color={Color.TRANSPARENT}
            label="Add"
            svg={plusSVG}
            onClick={() => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.JUXTAPOSITION))}
          />
          <div className="justify-items-end">
            {productCategories.map(({ code, name }) => {
              return (
                <div className="pb-2" key={code}>
                  <Tooltip key={code} tooltip={name} direction={TooltipDirection.BOTTOM}>
                    <Chip
                      label={name}
                      size={Size.MEDIUM}
                      textPriority={TextPriority.EQUAL}
                      onRemove={() => onRemove([name])}
                    />
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Juxtaposition;
